/**
 * @fileoverview A table row's three-dot ("kebab") actions menu.
 * @description Gathers every action a row offers behind one icon button, so a table can keep a
 * narrow Actions column however many actions a record allows. The dropdown renders into a portal
 * with fixed positioning, because tables here scroll horizontally inside an `overflow-x-auto`
 * wrapper that would otherwise clip it (most visibly on the last rows). It opens below the button,
 * or above it when there isn't room below, right-aligned to the button, and follows the button as
 * the page scrolls or resizes. Clicking outside or Escape closes it; ArrowUp/ArrowDown move between
 * items. Clicks inside never bubble
 * to the row, so a row that opens on click doesn't also open when the menu is used.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { toTestIdSegment } from '@/shared/lib/test-id/test-id.utils'

import type { KeyboardEvent, MouseEvent, RefObject } from 'react'

export interface RowAction {
  key: string
  label: string
  onSelect: () => void
  tone?: 'default' | 'danger'
}

interface RowActionsMenuProps {
  actions: RowAction[]
  /** Accessible name for the trigger button, e.g. "Actions for Sangli Urban Bank". */
  label: string
}

interface MenuPosition {
  top: number
  right: number
}

const MENU_GAP_PX = 6

/**
 * @description Closes the menu on a press outside both the trigger and the menu, and on Escape
 * (which also returns focus to the trigger).
 * @param {boolean} open - Whether the menu is open.
 * @param {() => void} close - Closes the menu.
 * @param {RefObject<HTMLButtonElement | null>} triggerRef - The trigger button.
 * @param {RefObject<HTMLDivElement | null>} menuRef - The menu element.
 */
function useDismissMenu(
  open: boolean,
  close: () => void,
  triggerRef: RefObject<HTMLButtonElement | null>,
  menuRef: RefObject<HTMLDivElement | null>,
) {
  useEffect(() => {
    if (!open) return undefined

    const onPointerDown = (event: globalThis.MouseEvent) => {
      const target = event.target as Node
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        close()
      }
    }
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
        triggerRef.current?.focus({ preventScroll: true })
      }
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open, close, triggerRef, menuRef])
}

/**
 * @description Keeps the open menu beside its trigger: places it before the first paint, then again
 * on every scroll (of the page or any scrolling table) and resize, since a fixed-position menu
 * would otherwise drift away from its row. Closing on scroll instead would also close it the moment
 * it opened whenever a scroll was still settling — e.g. one the browser starts to bring the button
 * into view.
 * @param {boolean} open - Whether the menu is open.
 * @param {RefObject<HTMLButtonElement | null>} triggerRef - The trigger button.
 * @param {RefObject<HTMLDivElement | null>} menuRef - The menu element.
 * @param {(position: MenuPosition) => void} setPosition - Stores the menu's position.
 */
function useFollowTrigger(
  open: boolean,
  triggerRef: RefObject<HTMLButtonElement | null>,
  menuRef: RefObject<HTMLDivElement | null>,
  setPosition: (position: MenuPosition) => void,
) {
  useLayoutEffect(() => {
    if (!open) return undefined

    const place = () => {
      if (triggerRef.current && menuRef.current) {
        setPosition(
          positionMenu(triggerRef.current.getBoundingClientRect(), menuRef.current.offsetHeight),
        )
      }
    }

    place()
    menuRef.current
      ?.querySelector<HTMLButtonElement>('[role="menuitem"]')
      ?.focus({ preventScroll: true })
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [open, triggerRef, menuRef, setPosition])
}

/**
 * @description Computes where the open menu sits: right-aligned to the trigger, below it, or above
 * it when the space below is shorter than the menu.
 * @param {DOMRect} trigger - The trigger button's bounding box.
 * @param {number} menuHeight - The rendered menu's height.
 * @returns {MenuPosition} The menu's fixed `top`/`right` offsets.
 */
function positionMenu(trigger: DOMRect, menuHeight: number): MenuPosition {
  const right = window.innerWidth - trigger.right
  const fitsBelow = trigger.bottom + MENU_GAP_PX + menuHeight <= window.innerHeight
  const top = fitsBelow
    ? trigger.bottom + MENU_GAP_PX
    : Math.max(MENU_GAP_PX, trigger.top - MENU_GAP_PX - menuHeight)
  return { top, right }
}

/**
 * @description Moves focus to the next or previous menu item on ArrowDown/ArrowUp, wrapping round.
 * @param {KeyboardEvent<HTMLDivElement>} event - The key event from the menu.
 */
function moveFocus(event: KeyboardEvent<HTMLDivElement>) {
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
  event.preventDefault()
  const items = Array.from(
    event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'),
  )
  const current = items.indexOf(document.activeElement as HTMLButtonElement)
  const step = event.key === 'ArrowDown' ? 1 : -1
  items[(current + step + items.length) % items.length]?.focus({ preventScroll: true })
}

/**
 * @description Renders the three-dot icon.
 */
function KebabIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="12" cy="5" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="12" cy="19" r="1.8" />
    </svg>
  )
}

/**
 * @component
 * @param {RowActionsMenuProps} props - The row's actions and the trigger's accessible name.
 * @param {RowAction[]} props.actions - The actions to list, in order. Renders nothing when empty.
 * @param {string} props.label - Accessible name for the trigger button.
 */
function RowActionsMenu({ actions, label }: RowActionsMenuProps) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<MenuPosition | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const close = useCallback(() => {
    setOpen(false)
    setPosition(null)
  }, [])

  useDismissMenu(open, close, triggerRef, menuRef)
  useFollowTrigger(open, triggerRef, menuRef, setPosition)

  if (actions.length === 0) return null

  /**
   * @description Stops a click from reaching the row behind it.
   * @param {MouseEvent} event - The click event.
   */
  const stopRowClick = (event: MouseEvent) => event.stopPropagation()

  return (
    <div onClick={stopRowClick} className="flex justify-end">
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        data-testid="row-actions-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-input-border bg-white text-slate hover:border-primary hover:text-primary"
      >
        <KebabIcon />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={label}
            data-testid="row-actions-menu"
            onClick={stopRowClick}
            onKeyDown={moveFocus}
            style={position ?? { top: 0, right: 0, visibility: 'hidden' }}
            className="fixed z-50 min-w-48 rounded-lg border border-divider bg-white py-1.5 shadow-[0_14px_36px_rgba(11,26,52,0.18)]"
          >
            {actions.map((action) => (
              <button
                key={action.key}
                type="button"
                role="menuitem"
                data-testid={`row-action-${toTestIdSegment(action.key)}`}
                onClick={() => {
                  close()
                  action.onSelect()
                }}
                className={`flex w-full cursor-pointer items-center px-3.5 py-2 text-left text-[13px] font-medium hover:bg-input-bg focus:bg-input-bg focus:outline-none ${
                  action.tone === 'danger' ? 'text-danger' : 'text-ink'
                }`}
              >
                {action.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  )
}

export default RowActionsMenu
